import React, { useMemo } from 'react';
import i18n from '../../../../../i18n';
import { fmt } from '../../../data/metrics';
import { fmtShortDate } from '../../../lib/growthFormat';
import type { DrillRepeatNoShows } from '../../../types';
import { DrillTile } from './DrillTile';
import '../../../styles/drill-noshow.css';

const tr = (key: string, opts?: Record<string, unknown>) =>
  i18n.t(`performance:noshowSubstance.repeat.${key}`, opts);

const TILE_ROWS = 4;

/**
 * Guests with repeated no-shows over twelve months and their share of this
 * period's no-shows. Each row carries aleno's guest id, which staff can search
 * for to add a note or ask for a card next time.
 */
export function DrillRepeatOffendersSection({
  repeat,
}: {
  repeat?: DrillRepeatNoShows | null;
}) {
  // One venue in scope: the venue column says nothing.
  const showVenue =
    new Set((repeat?.guests ?? []).map(g => g.venueId ?? '')).size > 1;

  const inspector = useMemo(
    () =>
      repeat ? (
        <>
          <p className="ddrill-ns-note">
            {repeat.repeatGuests
              ? tr('summary', {
                  guests: fmt.num(repeat.repeatGuests),
                  min: repeat.minNoshows,
                  count: fmt.num(repeat.repeatPeriodNoshows),
                  total: fmt.num(repeat.periodNoshows),
                })
              : tr('none', { min: repeat.minNoshows })}
          </p>
          {repeat.guests.length ? (
            <div className="ddrill-ns-table-wrap">
              <table className="ddrill-ns-table">
                <thead>
                  <tr>
                    <th>{tr('guestId')}</th>
                    {showVenue ? <th>{tr('venue')}</th> : null}
                    <th className="is-num">{tr('inPeriod')}</th>
                    <th className="is-num">{tr('noShows')}</th>
                    <th className="is-num">{tr('reservations')}</th>
                    <th>{tr('last')}</th>
                  </tr>
                </thead>
                <tbody>
                  {repeat.guests.map(g => (
                    <tr key={g.guestId}>
                      <td>
                        <span className="ddrill-ns-id" title={g.guestId}>
                          {g.guestId}
                        </span>
                      </td>
                      {showVenue ? <td>{g.venue ?? ''}</td> : null}
                      <td className="is-num">{fmt.num(g.noShowsInPeriod)}</td>
                      <td className="is-num">{fmt.num(g.noShows12m)}</td>
                      <td className="is-num">{fmt.num(g.reservations12m)}</td>
                      <td>{fmtShortDate(g.lastNoShow)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {repeat.truncated ? (
            <p className="ddrill-ns-note is-quiet">
              {tr('truncated', {
                shown: fmt.num(repeat.guests.length),
                total: fmt.num(repeat.repeatGuests),
              })}
            </p>
          ) : null}
          {repeat.guests.length ? (
            <p className="ddrill-ns-note is-quiet">{tr('lookup')}</p>
          ) : null}
        </>
      ) : null,
    [repeat, showVenue],
  );

  if (!repeat) return null;

  return (
    <DrillTile
      sectionId="repeat-noshows"
      span={4}
      title={tr('title')}
      meta={tr('meta', {
        min: repeat.minNoshows,
        n: fmt.num(repeat.periodNoshows),
      })}
      inspector={inspector}
    >
      {repeat.repeatGuests ? (
        <>
          <div className="ddrill-stat-inline">
            <strong style={{ color: 'var(--accent)' }}>
              {repeat.sharePct}%
            </strong>
            <span>{tr('share')}</span>
          </div>
          <p className="ddrill-ns-note">
            {tr('summary', {
              guests: fmt.num(repeat.repeatGuests),
              min: repeat.minNoshows,
              count: fmt.num(repeat.repeatPeriodNoshows),
              total: fmt.num(repeat.periodNoshows),
            })}
          </p>
          <div className="ddrill-ns-rows" style={{ marginTop: 8 }}>
            {repeat.guests.slice(0, TILE_ROWS).map(g => (
              <div className="ddrill-ns-row" key={g.guestId}>
                <span className="ddrill-ns-id" title={g.guestId}>
                  {g.guestId}
                </span>
                <span className="dcrux-si-name" style={{ fontWeight: 400 }}>
                  {showVenue ? (g.venue ?? '') : ''}
                </span>
                <span />
                <span className="ddrill-ns-sub">
                  {tr('row', {
                    noShows: fmt.num(g.noShows12m),
                    reservations: fmt.num(g.reservations12m),
                    inPeriod: fmt.num(g.noShowsInPeriod),
                    date: fmtShortDate(g.lastNoShow),
                  })}
                </span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="ddrill-ns-note">
          {tr('none', { min: repeat.minNoshows })}
        </p>
      )}
    </DrillTile>
  );
}
